import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { Action } from 'redux';
import { Spinner } from 'components/spinner/Spinner';
import { ListTable } from './listTable';
import { ListPagination } from './listPagination';
import { InfoTableApi } from './infoTableApi';
import { AddUser } from '../../pages/manageUsers/AddUser';
import WarningIcon from '../../images/Icon_Warning.png';
import NoResultsIcon from '../../images/Icon_No_Results.png';
import { BulkUpload } from '../../pages/manageUsers/BulkUpload';

type Props = {
  title?: string;
  listUrl: string;
  firstColWidth?: number;
  firstColWidthSm?: number;
  buttonText?: string;
  secondButtonText?: string;
  updateListStatus?: boolean;
  setUpdateList?: (status: boolean) => Action;
  dropdrownMenuOptions?: (
    ids: number,
    name: string,
    closeMenu: () => void,
    openCloseState: { open: boolean; id: number | null }
  ) => JSX.Element;
  setIsPanelClosed: React.Dispatch<React.SetStateAction<boolean>>;
  isPanelClosed: boolean;
  allowSelectAll?: boolean;
  filterTable?: boolean;
  setCheckTableLoading?: any;
  hideBtmCtrls?: boolean;
  hideSideMenu?: boolean;
  getSelectedOpts?: any;
  rowHighlight?: boolean;
  wondeRow?: boolean;
  wondeRowType?: string;
  hideChkboxCol?: boolean;
  setTableData?: any;
  setTableError?: Dispatch<SetStateAction<boolean>>;
  infoTxt?: string;
  children?: any;
  rowClassname?: (row: any) => string;
};

export const InfoTable: React.FC<Props> = ({
  title,
  listUrl,
  firstColWidth,
  firstColWidthSm,
  buttonText,
  secondButtonText,
  children,
  dropdrownMenuOptions,
  updateListStatus,
  setUpdateList,
  setIsPanelClosed,
  isPanelClosed,
  allowSelectAll = true,
  filterTable = true,
  setCheckTableLoading,
  hideBtmCtrls,
  hideSideMenu,
  getSelectedOpts,
  rowHighlight,
  wondeRow,
  wondeRowType,
  hideChkboxCol,
  setTableData,
  setTableError,
  infoTxt,
  rowClassname,
}) => {
  const [infoListData, setInfoListData] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [noResultsMessage, setNoResultsMessage] = useState<string | null>(null);
  const [listPanelOpen, setListPanelOpen] = useState(false);
  const [bulkUploadPopupOpen, setBulkUploadPopupOpen] = useState(false);

  useEffect(() => {
    if (!updateListStatus) return;

    loadInfoTableList(listUrl);
  }, [updateListStatus]);

  function loadInfoTableList(listUrl: string, paramsArr?: any) {
    if(setCheckTableLoading){
      setCheckTableLoading(true);
    }
    setLoading(true);
    setNoResultsMessage(null);
    setUpdateList && setUpdateList(false);

    (async () => {
      const listResults = await InfoTableApi.getInfoTableList(
        listUrl,
        paramsArr
      );

      if (listResults.data) {
        const infoTableData = listResults.data;

        if (
          infoTableData &&
          infoTableData.errors &&
          infoTableData.errors.length
        ) {
          setErrorMessage(
            'Oops, something went wrong. Please try again later.'
          );

          if(setTableError){
            setTableError(true);
          }
        } else if (
          infoTableData &&
          infoTableData.listData &&
          !infoTableData.listData.length
        ) {
          setNoResultsMessage('No results');
        } else {
          setErrorMessage(null);
        }

        if(setTableData && infoTableData.listData && infoTableData.listData.length > 0){
          setTableData(infoTableData.listData);
        }

        setInfoListData(infoTableData);
        if(setCheckTableLoading){
          setCheckTableLoading(false);
        }        
        setLoading(false);
      }
    })();
  }

  return (
    <React.Fragment>
      {title && (
        <>
          <div className="InfoTableHdr">
            <div className="LeftSide">
              <h1 className="PageTitle">{title}</h1>
            </div>
            {!loading && !errorMessage && buttonText && (
              <div className="RightSide">
                {secondButtonText &&
                  <div className="Btn SecondBtn" onClick={() => setBulkUploadPopupOpen(true)}>
                    {secondButtonText}
                  </div>              
                }              
                <div className="Btn" onClick={() => setListPanelOpen(true)}>
                  {buttonText}
                </div>
              </div>
            )}
          </div>        
          {infoTxt && 
            <>
              <p>{infoTxt}</p>
              <div className="LineSpacer5"></div>
            </>
          }
        </>
      )}

      <div className="InfoTableContainer">
        {loading ? (
          <div className="PageSpinner">
            <Spinner color={'#f59e16'} />
          </div>
        ) : errorMessage ? (
          <div className="FailedMsg">
            <div className="MsgContainer Error">
              <div className="Img">
                <img src={WarningIcon} alt="Warning" />
              </div>
              <div className="Txt">{errorMessage}</div>
            </div>
          </div>
        ) : (
          <React.Fragment>
            <div className="LTContainer">
              {wondeRow &&
                <div className="WondeLTTopRow">
                  <div className="TRCol Col1">
                    <h4>Wonde Records</h4>
                  </div>
                  <div className={"TRCol Col2 " + wondeRowType}>
                    <h4>Get Set 4 Education records</h4>
                  </div>              
                </div>
              }

              <ListTable
                infoListData={infoListData}
                firstColWidth={firstColWidth}
                firstColWidthSm={firstColWidthSm}
                listUrl={listUrl}
                loadInfoTableList={loadInfoTableList}
                dropdrownMenuOptions={dropdrownMenuOptions ?? null}
                setIsPanelClosed={setIsPanelClosed}
                isPanelClosed={isPanelClosed}
                allowSelectAll={allowSelectAll}
                filterTable={filterTable}
                noResultsMessage={noResultsMessage}
                hideSideMenu={hideSideMenu}
                getSelectedOpts={getSelectedOpts}
                rowHighlight={rowHighlight}
                hideChkboxCol={hideChkboxCol}
                rowClassname={rowClassname}
              >
                {children}
              </ListTable>
            </div>

            {noResultsMessage && 
              <div className="FailedMsg">
                <div className="MsgContainer NoResults">
                  <div className="Img">
                    <img src={NoResultsIcon} alt="No Results" />
                  </div>
                  <div className="Txt">{noResultsMessage}</div>
                </div>
              </div>
            }

            {!hideBtmCtrls &&
              <ListPagination
                listUrl={listUrl}
                infoListData={infoListData}
                loadInfoTableList={loadInfoTableList}
              />
            }
          </React.Fragment>
        )}
      </div>

      {listPanelOpen && (
        <AddUser panelOpen={listPanelOpen} togglePanelOpen={setListPanelOpen} />
      )}

      {bulkUploadPopupOpen && (
        <BulkUpload
          panelOpen={bulkUploadPopupOpen}
          togglePanelOpen={setBulkUploadPopupOpen}
        />        
      )}      
    </React.Fragment>
  );
};
